import { combineLatest, Observable } from 'rxjs';
import { createViewpointBuilderNavigation$ } from 'viewpointBuilder/viewpointBuilderNavigation/viewpointBuilderNavigation$';
import { ViewpointBuilderNavigationViewModel } from 'viewpointBuilder/viewpointBuilderNavigation/types';
import { createComponentSearch$ } from 'viewpointBuilder/selectContextTab/componentSearch$';
import { createMetamodelView$ } from 'viewpointBuilder/metaModel/metamodelView$';
import { createEditTraversals$ } from 'viewpointBuilder/traversals/editTraversals$';
import { createMetaInfo$ } from 'viewpointBuilder/viewpointMetaInfo/viewpointMetaInfo$';
import { createViewpointGroupingRules$ } from 'viewpointBuilder/addGroupingRulesTab/viewpointGroupingRules$';
import {
  createViewpointFormatting$,
  createViewpointFormattingState$,
} from 'viewpointBuilder/formattingTabs/viewpointFormatting$';
import { createSelectedView$ } from 'viewpointBuilder/selectedView/selectedView$';
import { SelectedViewState } from 'viewpointBuilder/selectedView/types';
import { ComponentSearchViewState } from 'viewpointBuilder/selectContextTab/types';
import { MetamodelViewState } from 'viewpointBuilder/metaModel/types';
import { TraversalBuilderViewState } from 'viewpointBuilder/types';
import { MetaInfoState } from 'viewpointBuilder/viewpointMetaInfo/metaInfoTypes';
import { GroupingRulesState } from 'viewpointBuilder/addGroupingRulesTab/groupingRulesTypes';
import {
  ViewpointFormattingProps,
  ViewpointFormattingState,
} from 'viewpointBuilder/formattingTabs/viewpointFormattingTypes';
import {
  ViewpointBuilderState,
  ViewpointBuilderViewState,
} from 'viewpointBuilder/types';
import { getPrimaryButtonConfig } from './getPrimaryButtonConfig';
import { map } from 'rxjs/operators';
import { i18nLocale$ } from 'streams/i18nLocale$';
import { locale$ } from 'streams/locale$';

export type ViewpointBuilderStreams = {
  namespace: string;
  viewpointBuilderNavigation$: Observable<ViewpointBuilderNavigationViewModel>;
  componentSearch$: Observable<ComponentSearchViewState>;
  metamodelView$: Observable<MetamodelViewState>;
  editTraversals$: Observable<TraversalBuilderViewState>;
  selectedView$: Observable<SelectedViewState>;
  viewpointFormattingState$: Observable<ViewpointFormattingState>;
  viewpointFormatting$: Observable<ViewpointFormattingProps>;
  viewpointGroupingRules$: Observable<GroupingRulesState>;
  metaInfo$: Observable<MetaInfoState>;
};

// Builds each of ViewpointBuilder's per-namespace streams. reducedStream
// doesn't dedupe by name, so consumers must reuse these instances rather
// than calling the factories again for the same namespace.
export const createViewpointBuilderStreams = (
  namespace: string
): ViewpointBuilderStreams => {
  const selectedView$ = createSelectedView$(namespace);
  const viewpointFormattingState$ = createViewpointFormattingState$(
    namespace,
    selectedView$
  );
  const viewpointFormatting$ = createViewpointFormatting$(
    namespace,
    viewpointFormattingState$
  );
  const viewpointGroupingRules$ = createViewpointGroupingRules$(
    namespace,
    selectedView$
  );
  const metaInfo$ = createMetaInfo$(
    namespace,
    selectedView$,
    viewpointFormatting$,
    viewpointGroupingRules$
  );

  const streams: ViewpointBuilderStreams = {
    namespace,
    viewpointBuilderNavigation$: createViewpointBuilderNavigation$(namespace),
    componentSearch$: createComponentSearch$(namespace),
    metamodelView$: createMetamodelView$(namespace),
    editTraversals$: createEditTraversals$(namespace),
    selectedView$,
    viewpointFormattingState$,
    viewpointFormatting$,
    viewpointGroupingRules$,
    metaInfo$,
  };
  return streams;
};

/**
 * This stream is not a persistent state stream, but a stream that combines all the necessary state streams for the viewpoint builder.
 * We subscribe it in the Viewpoint Builder modal using withOnFirstSubscription. This means that once the modal is closed, the stream is unsubscribed and cleaned up.
 */
export const combineViewpointBuilderStreams = (
  streams: ViewpointBuilderStreams
): Observable<ViewpointBuilderViewState> =>
  combineLatest({
    viewpointBuilderNavigationState: streams.viewpointBuilderNavigation$,
    searchComponentsState: streams.componentSearch$,
    metamodelViewState: streams.metamodelView$,
    editTraversalsState: streams.editTraversals$,
    metaInfoState: streams.metaInfo$,
    groupingsState: streams.viewpointGroupingRules$,
    formattingState: streams.viewpointFormatting$,
    locale: locale$,
    i18nLocale: i18nLocale$,
  }).pipe(
    map((state: ViewpointBuilderState) => {
      return {
        ...state,
        primaryButtonConfig: getPrimaryButtonConfig(state, streams.namespace),
        namespace: streams.namespace,
      };
    })
  );

export const createViewpointBuilder$ = (
  namespace: string
): Observable<ViewpointBuilderViewState> =>
  combineViewpointBuilderStreams(createViewpointBuilderStreams(namespace));
